import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Itinerary, DynamicFormComponent, CataloguesService, ToastService } from '../../core';
import { ItinerariesQuestionsService } from '../services/itineraries-questions.service';
import { ProfileService, ItinerariesService, CoursesService } from 'core/services';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DocumentsManagerService } from 'core/services/documents-manager.service';

@Component({
  selector: 'lms-itinerary-editor',
  templateUrl: './itinerary-editor.component.html',
  styleUrls: ['./itinerary-editor.component.scss'],
  providers: [ItinerariesQuestionsService]
})
export class ItineraryEditorComponent implements OnInit {
  @Input() itinerary: Itinerary;
  public questions: any[];
  @Output() onSave = new EventEmitter<any>();
  @ViewChild(DynamicFormComponent, { static: false }) public dynamicForm: DynamicFormComponent;
  public user: any;
  public client: any;
  public partnerClient: any;
  public catalogId: string;
  public clientView: boolean = false;
  public lang: any;
  public courses: any;
  public bannerId: string;
  public cardImageId: any;
  public canEditItinerary: boolean = false;
  public defaultAccessMode: string;

  constructor(
    public itinerariesQuestion: ItinerariesQuestionsService,
    public activatedRoute: ActivatedRoute,
    public itinerariesService: ItinerariesService,
    public profileService: ProfileService,
    public toastService: ToastService,
    public documentsManagerService: DocumentsManagerService,
    public coursesService: CoursesService,
    private cataloguesService: CataloguesService,
    private translate: TranslateService
  ) {
    this.profileService.getLoggedUser$().subscribe((user) => {
      this.user = user;
      this.lang = user.lang;
    });
  }

  public ngOnInit(): void {
    this.activatedRoute.data.subscribe((data) => {
      const { courses: catalog, client, partnerClient } = data;

      if (catalog) {
        this.catalogId = catalog.id;
        this.courses = catalog.courses;
        this.defaultAccessMode = catalog.defaultAccessMode;
      }
      this.clientView = !!client;
      this.client = client ? client : null;
      this.partnerClient = partnerClient;
      this.canEditItinerary = this.itinerariesQuestion.canUserEditItinerary(this.itinerary, client);

      this.setQuestions();
    });
  }

  private async setQuestions(): Promise<any> {
    if (this.itinerary?.clientId && this.user.clientId !== this.itinerary.clientId) {
      const catalogue = await this.coursesService.getClientCourses(this.itinerary.clientId, true);
      this.courses = catalogue.courses;
    }
    this.questions = this.itinerariesQuestion.getQuestions({
      itinerary: this.itinerary,
      client: this.client,
      partnerClient: this.partnerClient,
      courses: this.courses,
      defaultAccessMode: this.defaultAccessMode
    });
  }

  public async submit(itineraryData: any): Promise<any> {
    let savedItinerary = {};
    if (this.canEditItinerary) {
      const tempItinerary = await this.parseDataToItinerary(itineraryData);
      if (this.itinerary) {
        savedItinerary = await this.onUpdate(tempItinerary);
      } else {
        savedItinerary = await this.onCreate(tempItinerary);
      }
    }

    savedItinerary = { ...this.itinerary, ...savedItinerary };

    if (this.clientView) {
      const catalogItem = await this.onUpdateCatalogItem(itineraryData['_itineraryConfig'], savedItinerary);
      savedItinerary = { ...savedItinerary, ...catalogItem };
    }
    this.onSave.emit(savedItinerary);
  }

  private async parseDataToItinerary(data: any) {
    await this.chooseImgToUpload(data['_itineraryConfig']);
    const tempItineraryConfigInfo: Itinerary = Object.assign(data['_itineraryConfig'], {
      modules: data['_itineraryConfig']['modules'].map((course) => {
        return { courseId: course.id };
      }),
      cardImageId: this.cardImageId,
      bannerId: this.bannerId
    });
    const tempItineraryBasicInfo: Itinerary = Object.assign({}, data['_itineraryInfo']);
    const tempItinerary = Object.assign({}, tempItineraryConfigInfo, tempItineraryBasicInfo);
    return tempItinerary;
  }

  private async onCreate(itinerary: Itinerary): Promise<any> {
    itinerary.aimedAt = '';
    if (itinerary.bannerMobilePosition === '') {
      itinerary.bannerMobilePosition = 0;
    }
    if (this.isEmptyDocumentation(itinerary.documentation)) {
      itinerary.documentation = null;
    }
    return this.itinerariesService
      .createItinerary(itinerary, itinerary.lang)
      .then((savedItinerary) => {
        this.saveDocumentation(itinerary, savedItinerary);
        this.endSubmit(null);
        return savedItinerary;
      })
      .catch((err) => {
        console.error(err);
        this.toastService.showError(err);
        this.endSubmit(err);
      });
  }

  private async onUpdate(updatedData: any): Promise<any> {
    if (updatedData.bannerMobilePosition === '') {
      updatedData.bannerMobilePosition = 0;
    }
    await this.chooseImgToUpload(updatedData);

    if (this.isEmptyDocumentation(updatedData.documentation)) {
      updatedData.documentation = null;
    }
    const itinerary: Itinerary = Object.assign(this.itinerary, updatedData, {
      cardImageId: this.cardImageId,
      bannerId: this.bannerId
    });
    return this.itinerariesService
      .updateItinerary(itinerary, this.client?.id)
      .then((updatedItinerary) => {
        this.saveDocumentation(this.itinerary, updatedItinerary);
        this.endSubmit(null);
        return updatedItinerary;
      })
      .catch((err) => {
        console.error(err);
        this.toastService.showError(err);
        this.endSubmit(err);
      });
  }

  private async onUpdateCatalogItem(updatedData: any, itinerary: any): Promise<any> {
    const currentLang = this.translate.currentLang;
    const catalogItemData = {
      ...updatedData,
      courseId: itinerary.id
    };
    await this.cataloguesService.updateCatalogItem(this.catalogId, catalogItemData, true, currentLang);
    return updatedData;
  }

  onPublish(publishedItinerary) {
    this.itinerary = publishedItinerary;
    this.onSave.emit(this.itinerary);
    this.endSubmit(null);
  }

  private endSubmit(value): void {
    this.dynamicForm.submitted(value);
  }

  saveDocumentation(itineraryData, savedItinerary) {
    if (this.isEmptyDocumentation(itineraryData)) return;
    const { documentation = {} } = itineraryData;
    if (
      documentation &&
      documentation.hasChanges &&
      documentation.type === 'pdf' &&
      documentation._document &&
      savedItinerary?.documentationUploadUrl
    ) {
      this.documentsManagerService
        .uploadS3File(savedItinerary.documentationUploadUrl, documentation._document)
        .subscribe();
    }
  }

  private async uploadImg(imgData: any, compress: { quality: number; width: number }): Promise<any> {
    return await this.documentsManagerService.uploadDocument(imgData, compress);
  }

  private async chooseImgToUpload(itineraryData: any): Promise<any> {
    if (itineraryData.cardImg && (!this.itinerary || itineraryData.cardImg !== this.itinerary.cardImageUrl)) {
      this.cardImageId = await this.uploadImg(itineraryData.cardImg, { quality: 0.85, width: 540 });
    }
    if (itineraryData.bannerImg && (!this.itinerary || itineraryData.bannerImg !== this.itinerary.bannerUrl)) {
      this.bannerId = await this.uploadImg(itineraryData.bannerImg, { quality: 0.85, width: 2560 });
    }
  }

  isEmptyDocumentation(documentation) {
    if (documentation && documentation.url == '' && !documentation._document) return true;
    return false;
  }
}
